import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowRight, Leaf, Flower2, Salad, CloudSun, Activity, MoonStar, Sparkles, Palmtree } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { LeafMark } from "@/components/LeafMark";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";

const CATEGORY_ICONS = [Sparkles, Flower2, Salad, CloudSun, Activity, Leaf, MoonStar, Palmtree];

export const Wellness = () => {
    const {  t, tHtml, site: siteData } = useLang();
    const { WELLNESS_CATEGORIES, WHY_INDIA_WELLNESS } = siteData;
    usePageMeta(
        t("meta.wellness.title", "Wellness in India & Kerala — Personalised Wellness Journeys"),
        t("meta.wellness.desc", "Personalised wellness experiences in India — yoga, meditation, nutrition, lifestyle programs and restorative retreats — coordinated by UniversHeal, starting in Kerala.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("wellness.eyebrow", "Wellness")}
                title={
                    <>
                        Wellness,{" "}
                        <span className="italic text-pine">designed around you.</span>
                    </>
                }
                intro={t("wellness.intro", "Step away from the pace of everyday life and discover personalised wellness experiences in India — from yoga and meditation to nutrition, lifestyle and restorative retreats.")}
                testid="wellness-hero"
            />

            {/* A. Wellness journeys */}
            <section className="section-pad pt-10">
                <div className="container-site grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
                    <Reveal className="h-full">
                        <div className="h-full min-h-[300px] rounded-2xl overflow-hidden shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.keralaGhats}
                                alt="Green mountain ridges of the Western Ghats in Kerala under soft morning mist"
                                data-testid="wellness-journey-image"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1} className="h-full">
                        <div className="h-full bg-forest text-ivory rounded-2xl p-8 sm:p-10 flex flex-col justify-center">
                            <LeafMark className="w-7 h-7 text-natural" />
                            <h2 className="mt-5 font-serif font-medium text-2xl sm:text-3xl leading-snug">
                                A journey shaped around{" "}
                                <span className="italic text-natural">your goals.</span>
                            </h2>
                            <p className="mt-4 text-sm leading-relaxed text-ivory/70 max-w-md">
                                {t("wellness.text1", "Whether you are seeking deep rest, a healthier routine, time to switch off or a longer immersive retreat — tell us what you are looking for and we will help coordinate a wellness experience in India that fits your goals, pace and comfort.")}
                            </p>
                            <p className="mt-4 text-xs leading-relaxed text-ivory/55 max-w-md">
                                {t("wellness.text2", "Wellness and Ayurvedic services may vary by provider and individual circumstances. If you have medical conditions, discuss any program with your doctor first.")}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* B. Wellness categories */}
            <section className="section-pad bg-white" data-testid="wellness-categories">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("wellness.eyebrow", "Wellness Categories")}
                        title={
                            <>
                                What are you looking{" "}
                                <span className="italic text-pine">space for?</span>
                            </>
                        }
                        intro={t("wellness.intro", "Eight broad directions — most wellness journeys combine more than one.")}
                    />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {WELLNESS_CATEGORIES.map((cat, i) => {
                            const Icon = CATEGORY_ICONS[i % CATEGORY_ICONS.length];
                            return (
                                <Reveal key={cat.title} delay={i * 0.05} className="h-full">
                                    <div
                                        data-testid={`wellness-category-${i + 1}`}
                                        className="h-full bg-ivory border border-pine/10 rounded-2xl p-7 card-lift"
                                    >
                                        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-sage text-forest">
                                            <Icon className="w-5 h-5" strokeWidth={1.5} />
                                        </span>
                                        <h3 className="mt-5 font-serif font-medium text-xl text-forest leading-snug">
                                            {cat.title}
                                        </h3>
                                        <p className="mt-2 text-sm leading-relaxed text-moss">{cat.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* C. Why India for Wellness */}
            <section className="section-pad bg-sage/50" data-testid="why-india-wellness">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("wellness.eyebrow", "Why India for Wellness")}
                        title={t("wellness.title1", "Traditions of wellbeing, lived daily.")}
                        intro={t("wellness.intro", "India's wellness culture is lived, not performed — woven into food, mornings, festivals and family life. For visitors, that depth translates into experiences that feel genuine rather than staged.")}
                    />
                    <div className="mt-12 max-w-4xl">
                        {WHY_INDIA_WELLNESS.map((point, i) => (
                            <Reveal key={point.title} delay={i * 0.05}>
                                <div
                                    data-testid={`wellness-india-point-${i + 1}`}
                                    className="border-t border-pine/15 py-7 grid grid-cols-[2.5rem_1fr] gap-4 sm:gap-10"
                                >
                                    <span className="font-serif italic text-2xl text-natural leading-none pt-1">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div>
                                        <h3 className="font-serif font-medium text-2xl text-forest leading-snug">{point.title}</h3>
                                        <p className="mt-2 text-sm leading-relaxed text-moss max-w-2xl">{point.desc}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* D. Kerala Wellness */}
            <section className="section-pad bg-white" data-testid="kerala-wellness">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("wellness.eyebrow", "Kerala Wellness")}
                        title={
                            <>
                                Kerala — the quiet heart of{" "}
                                <span className="italic text-pine">wellbeing in India.</span>
                            </>
                        }
                        intro={t("wellness.intro", "Kerala is where UniversHeal's wellness offering begins: a state of green highlands, still backwaters and a long coastline — where wellness traditions are part of everyday life and hospitality is a craft.")}
                    />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            { img: IMG.keralaMunnar, alt: "Sunrise over tea plantations in the Kerala highlands", cap: "Lush highland landscapes", id: "kwl-munnar" },
                            { img: IMG.keralaBackwaters, alt: "Still Kerala backwaters reflecting coconut palms at golden hour", cap: "Calm backwater settings", id: "kwl-backwaters" },
                            { img: IMG.keralaCoast, alt: "Golden sunset over coastal cliffs and the Arabian Sea in Kerala", cap: "A restorative coastline", id: "kwl-coast" },
                        ].map((item, i) => (
                            <Reveal key={item.id} delay={i * 0.08} className="h-full">
                                <figure
                                    data-testid={`kerala-wellness-${item.id}`}
                                    className="h-full bg-ivory border border-pine/10 rounded-2xl overflow-hidden card-lift"
                                >
                                    <div className="h-56 overflow-hidden">
                                        <img src={item.img} alt={item.alt} className="w-full h-full object-cover" loading="lazy" />
                                    </div>
                                    <figcaption className="p-6 font-serif italic text-xl text-forest">{item.cap}</figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <div className="mt-12 bg-ivory border border-pine/10 rounded-2xl p-8 sm:p-10">
                            <p className="eyebrow text-forest text-[11px]">Our Launch Destination</p>
                            <p className="mt-4 text-sm leading-relaxed text-moss max-w-3xl">
                                {t("wellness.text3", "Kerala is UniversHeal's initial destination — for medical care, wellness and Ayurveda — with plans to expand across India. Wellness environments shown here illustrate the character of the")}
                                region; specific centres and programs are shared
                                {t("wellness.text4", "once we understand your goals.")}
                            </p>
                            <Link
                                to="/contact"
                                data-testid="wellness-plan-cta"
                                className="btn-primary mt-8 group"
                            >
                                {t("wellness.text5", "Plan Your Wellness Journey")}
                                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("wellness.headline", "Rest is a plan, too.")}
                text={t("wellness.text", "Tell us what restoration looks like for you — we will help you shape a wellness experience in India around it.")}
            />
        </>
    );
};
